import * as React from "react";
import { useQuery } from "@apollo/client";
import ClientSelector from "./client-selector/Container";
import { MeetingPageStage, StageRenderer } from "./types";
import { RootContainer } from "./widgets";
import { MeetingViewQuery, MeetingViewQueryVariables, MeetingViewDocument } from "../queries.generated";
import PreparationDialog from "./preparation/Container";
import { CurrentMeeting, CurrentMeetingContext } from "./contexts/currentMeeting";
import { MeetingPageSettingsProvider, meetingMediaPreferencesStorage, meetingPageSettingsStorage, useSettings } from "./tools";
import LoadingIcon from "../svg-icons/LoadingIcon";
import LiftOff from "./LiftOff";
import { meetingIsRunning, NotFoundError } from "./meetingDataErrors";
import { MeetingIsNotRunning } from "./MeetingIsNotRunning";
import { MeetingRoom } from "./room/Room";


const stageRenderers: { [k in MeetingPageStage]: StageRenderer } = {
    [MeetingPageStage.SelectClient]: ClientSelector,
    [MeetingPageStage.Prepare]: PreparationDialog,
    [MeetingPageStage.Room]: MeetingRoom,
    [MeetingPageStage.LiftOff]: LiftOff,
};

const Root = ({ id }: { id: number }) => {
    const [, recheckMeetingAvailability] = React.useReducer((version) => version + 1, 0);
    const { data, error } = useQuery<MeetingViewQuery, MeetingViewQueryVariables>(MeetingViewDocument, {
        variables: { meetingId: id },
    });
    const settings = useSettings();

    React.useLayoutEffect(() => {
        const { audio, video } = meetingMediaPreferencesStorage.getSnapshot();
        settings.update({ audio, video });
    }, [settings.update]);

    if (error)
        // error is handled somewhere else (apollo-client/errors)
        return <RootContainer />;

    if (!data)
        return (
            <RootContainer
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <LoadingIcon />
            </RootContainer>
        );

    if (data.meetings.items.length === 0) return <NotFoundError />;

    const meeting = data.meetings.items[0];

    if (!meetingIsRunning(meeting))
        return <MeetingIsNotRunning meeting={meeting} onJoin={recheckMeetingAvailability} />;

    const Renderer: StageRenderer = stageRenderers[settings.stage];

    return (
        <RootContainer>
            <CurrentMeetingContext.Provider value={new CurrentMeeting(meeting, data.invitations.items)}>
                <Renderer />
            </CurrentMeetingContext.Provider>
        </RootContainer>
    );
};

export default ({ id }: { id: number }) => {
    return (
        <MeetingPageSettingsProvider settings={meetingPageSettingsStorage}>
            <Root id={id} />
        </MeetingPageSettingsProvider>
    );
};
